/**
 * Brand-logo path helper.
 *
 * The former per-security SVG-logo system (a hand-maintained ticker→file
 * allow-list, the UK-ticker→logo map, and fund-provider detection) was demo
 * residue (ADR-0009) and is gone — security identity now comes from the real
 * OpenFIGI-backed `security_reference` cache and renders as a monogram via
 * `CompanyLogo`. This module retains only the Septima and Dowgate logo paths used in
 * the sidebar, mobile top bar, apply page and the auth screens.
 *
 * Each path goes through {@link publicAssetPath}, because these files live in
 * `public/` and Vite cannot rebase a path that a TypeScript module holds as
 * a string. Under a base path an absolute path here answers 404.
 */

import { publicAssetPath } from './public-asset';

/**
 * Theme-aware path to the Septima icon mark. The asset files are pre-coloured
 * (black on `light`, white on `dark`) rather than recoloured via CSS `invert`,
 * so the caller passes the resolved theme and gets the correctly-filled file.
 */
export function getSeptimaMarkPath(resolvedTheme: 'light' | 'dark'): string {
    return resolvedTheme === 'dark'
        ? publicAssetPath('/logos/septima-mark-dark.svg')
        : publicAssetPath('/logos/septima-mark-light.svg');
}

/**
 * Theme-aware path to the full Septima wordmark (icon + lettering). Same
 * pre-coloured convention as {@link getSeptimaMarkPath}.
 */
export function getSeptimaWordmarkPath(resolvedTheme: 'light' | 'dark'): string {
    return resolvedTheme === 'dark'
        ? publicAssetPath('/logos/septima-wordmark-dark.svg')
        : publicAssetPath('/logos/septima-wordmark-light.svg');
}

/** Path to the Dowgate firm logo (single navy asset, theme-agnostic). */
export function getDowgateLogoPath(): string {
    return publicAssetPath('/logos/dowgate.svg');
}
